import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FileDown, Search } from 'lucide-react';
import { motion } from 'framer-motion';

const TranscriptExporter = ({ reducedMotion = false }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [filters, setFilters] = useState({
    intent: '',
    keyword: '',
    sentiment_min: '',
    sentiment_max: '',
    limit: 100
  });
  const [loading, setLoading] = useState(false);
  const [resultCount, setResultCount] = useState(null);
  const animationDuration = reducedMotion ? 0 : 0.3;

  const handleExport = async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params = new URLSearchParams();
      if (filters.intent) params.append('intent', filters.intent);
      if (filters.keyword) params.append('keyword', filters.keyword);
      if (filters.sentiment_min) params.append('sentiment_min', filters.sentiment_min);
      if (filters.sentiment_max) params.append('sentiment_max', filters.sentiment_max);
      params.append('limit', filters.limit);
      
      const response = await fetch(`${BACKEND_URL}/api/export/transcripts?${params.toString()}`);
      const csvData = await response.text();
      
      // Count rows (excluding header)
      const rowCount = csvData.split('\n').length - 2;
      setResultCount(rowCount);
      
      // Trigger download
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transcripts_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
          <FileDown className="w-5 h-5 text-teal-400" />
          Transcript Exporter
        </CardTitle>
        <p className="text-xs text-slate-400 mt-1">
          Filter and export specific conversation segments
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Intent Filter */}
            <div>
              <label className="text-xs text-slate-400 mb-2 block">Intent Filter</label>
              <Select value={filters.intent || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, intent: v === 'all' ? '' : v }))}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue placeholder="All intents" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All intents</SelectItem>
                  <SelectItem value="question">Question</SelectItem>
                  <SelectItem value="interested">Interested</SelectItem>
                  <SelectItem value="not_interested">Not Interested</SelectItem>
                  <SelectItem value="dnc">DNC</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Keyword Search */}
            <div>
              <label className="text-xs text-slate-400 mb-2 block">Keyword Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  value={filters.keyword}
                  onChange={(e) => setFilters(prev => ({ ...prev, keyword: e.target.value }))}
                  placeholder="Search transcripts..."
                  className="pl-10 bg-slate-800 border-slate-700"
                />
              </div>
            </div>

            {/* Sentiment Range */}
            <div>
              <label className="text-xs text-slate-400 mb-2 block">Min Sentiment</label>
              <Input
                type="number"
                min="-1"
                max="1"
                step="0.1"
                value={filters.sentiment_min}
                onChange={(e) => setFilters(prev => ({ ...prev, sentiment_min: e.target.value }))}
                placeholder="-1.0 to 1.0"
                className="bg-slate-800 border-slate-700"
              />
            </div>

            <div>
              <label className="text-xs text-slate-400 mb-2 block">Max Sentiment</label>
              <Input
                type="number"
                min="-1"
                max="1"
                step="0.1"
                value={filters.sentiment_max}
                onChange={(e) => setFilters(prev => ({ ...prev, sentiment_max: e.target.value }))}
                placeholder="-1.0 to 1.0"
                className="bg-slate-800 border-slate-700"
              />
            </div>
          </div>

          {/* Export Button */}
          <motion.div
            whileHover={reducedMotion || loading ? {} : { scale: 1.02 }}
            whileTap={reducedMotion || loading ? {} : { scale: 0.98 }}
          >
            <Button
              onClick={handleExport}
              disabled={loading}
              className="w-full bg-gradient-to-r from-teal-500 to-gold hover:from-teal-600 to-gold text-white font-medium"
            >
              {loading ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Exporting...
                </>
              ) : (
                <>
                  <FileDown className="w-4 h-4 mr-2" />
                  Export Transcripts (CSV)
                </>
              )}
            </Button>
          </motion.div>

          {/* Result Message */}
          {resultCount !== null && (
            <motion.div
              initial={reducedMotion ? {} : { opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center text-sm text-green-400 bg-green-500/10 p-3 rounded-lg border border-green-500/20"
            >
              ✓ Exported {resultCount} transcripts successfully
            </motion.div>
          )}

          {/* Info */}
          <div className="mt-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <p className="text-xs text-slate-400">
              <strong className="text-slate-300">Note:</strong> Transcripts are exported with all filters applied. 
              Maximum {filters.limit} results per export. Sensitive data may be redacted based on permissions.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TranscriptExporter;
